import { useState, useEffect, useRef, Fragment } from 'react'
import { supabase } from '../lib/supabase'
import ItemAttachments from './portal/ItemAttachments'

const SUPABASE_URL      = import.meta.env.VITE_SUPABASE_URL      || ''
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || ''
const EBIS_URL          = `${SUPABASE_URL}/functions/v1/ebis-workorder`

function fmtHrs(n) {
  if (n == null || n === '') return '—'
  const num = Number(n)
  return isNaN(num) ? '—' : num.toFixed(2)
}

function fmtMoney(n) {
  if (n == null || n === '') return '—'
  const num = Number(n)
  if (isNaN(num)) return '—'
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })
}

function fmtDateTime(s) {
  if (!s) return '—'
  try {
    return new Date(s.replace(' ', 'T')).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    })
  } catch { return s }
}

function fmtDateShort(s) {
  if (!s) return '—'
  try { return new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }
  catch { return s }
}

function fmtDateHeading(isoDate) {
  if (!isoDate) return '—'
  try {
    return new Date(isoDate + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    })
  } catch { return isoDate }
}

const REJECTED_ITEM_STATUSES = new Set([
  'Rejected', 'Customer Declined', 'Declined', 'N/A', 'Void',
])

const STATUS_COLORS = {
  'Open':                  'bg-blue-100 text-blue-700',
  'Finished':              'bg-green-100 text-green-700',
  'Waiting For Parts':     'bg-yellow-100 text-yellow-700',
  'Waiting on Parts':      'bg-yellow-100 text-yellow-700',
  'On Hold':               'bg-gray-100 text-gray-600',
  'Bill Sent/Completed':   'bg-emerald-100 text-emerald-700',
  'Pre-Maintenance':       'bg-purple-100 text-purple-700',
}

function StatusBadge({ status }) {
  const cls = STATUS_COLORS[status] || 'bg-gray-100 text-gray-500'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${cls}`}>
      {status || '—'}
    </span>
  )
}

const PART_STATUS_COLORS = {
  'Fulfilled':   'bg-green-100 text-green-700',
  'WIP':         'bg-blue-100 text-blue-700',
  'PO: Ordered': 'bg-amber-100 text-amber-700',
  'Received':    'bg-green-100 text-green-700',
  'On Order':    'bg-amber-100 text-amber-700',
  'Requested':   'bg-orange-100 text-orange-700',
  'Backordered': 'bg-red-100 text-red-700',
}

function PartStatusBadge({ status }) {
  const cls = PART_STATUS_COLORS[status] || 'bg-gray-100 text-gray-500'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${cls}`}>
      {status || '—'}
    </span>
  )
}

function CostStat({ label, value, highlight }) {
  return (
    <div className={`flex flex-col gap-0.5 ${highlight ? 'text-gray-900' : 'text-gray-600'}`}>
      <span className="text-xs text-gray-400 uppercase tracking-wide font-medium">{label}</span>
      <span className={`text-sm tabular-nums font-semibold ${highlight ? 'text-gray-900' : ''}`}>{value}</span>
    </div>
  )
}

export default function EBISWorkOrderPanel({ workOrderNumber, highlightItemNum, onClose, projectId, profileId }) {
  const [data, setData]         = useState(null)
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState(null)
  const [tab, setTab]           = useState('items')
  const [cachedAt, setCachedAt] = useState(null)
  const [flashedItem, setFlashedItem] = useState(null)
  const [attachments, setAttachments]         = useState([])
  const [expandedAttachRows, setExpandedAttachRows] = useState(new Set())
  const [expandedPartsRows, setExpandedPartsRows]   = useState(new Set())
  const bodyScrollRef = useRef(null)

  const [resolvedProjectId, setResolvedProjectId] = useState(projectId ?? null)
  const [rejectedItemNums, setRejectedItemNums]   = useState(new Set())

  useEffect(() => {
    if (projectId) {
      setResolvedProjectId(projectId)
      return
    }
    if (!workOrderNumber) { setResolvedProjectId(null); return }
    supabase.from('projects').select('id').eq('work_order', workOrderNumber).is('deleted_at', null).maybeSingle()
      .then(({ data: proj }) => setResolvedProjectId(proj?.id ?? null))
  }, [projectId, workOrderNumber])

  useEffect(() => {
    if (!workOrderNumber) { setRejectedItemNums(new Set()); return }
    supabase.from('customer_item_approvals')
      .select('item_num')
      .eq('work_order', workOrderNumber)
      .eq('decision', 'rejected')
      .then(({ data: rows }) => setRejectedItemNums(new Set((rows ?? []).map(r => r.item_num))))
  }, [workOrderNumber])

  useEffect(() => {
    if (!resolvedProjectId) { setAttachments([]); return }
    supabase.from('item_attachments').select('*').eq('project_id', resolvedProjectId).order('created_at')
      .then(({ data: rows }) => setAttachments(rows || []))
  }, [resolvedProjectId])

  useEffect(() => {
    if (!workOrderNumber) return
    setLoading(true)
    setError(null)
    setData(null)
    setCachedAt(null)
    setTab('items')

    const doLive = () => {
      fetch(`${EBIS_URL}?endpoint=detail`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ workOrderNumber }),
      })
        .then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e.error || `Error ${r.status}`)))
        .then(d => setData(d))
        .catch(err => setError(typeof err === 'string' ? err : 'Failed to load work order'))
        .finally(() => setLoading(false))
    }

    supabase
      .from('ebis_wo_cache')
      .select('payload, fetched_at')
      .eq('work_order', workOrderNumber)
      .limit(1)
      .maybeSingle()
      .then(({ data: row }) => {
        if (row) {
          const STALE_MS = 16 * 60 * 60 * 1000
          const isStale  = (Date.now() - new Date(row.fetched_at).getTime()) > STALE_MS
          if (isStale) {
            doLive()
          } else {
            setData(row.payload)
            setCachedAt(row.fetched_at)
            setLoading(false)
          }
        } else {
          doLive()
        }
      })
      .catch(() => doLive())
  }, [workOrderNumber])

  useEffect(() => {
    if (!data || highlightItemNum == null) return
    setTab('items')
    const raf = requestAnimationFrame(() => {
      if (!bodyScrollRef.current) return
      const row = bodyScrollRef.current.querySelector(`[data-itemnum="${highlightItemNum}"]`)
      if (!row) return
      row.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setFlashedItem(Number(highlightItemNum))
      setTimeout(() => setFlashedItem(null), 2000)
    })
    return () => cancelAnimationFrame(raf)
  }, [data, highlightItemNum])

  const handleRefresh = () => {
    if (!workOrderNumber || loading) return
    setLoading(true)
    setError(null)
    fetch(`${EBIS_URL}?endpoint=detail`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ workOrderNumber }),
    })
      .then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e.error || `Error ${r.status}`)))
      .then(d => { setData(d); setCachedAt(null) })
      .catch(err => setError(typeof err === 'string' ? err : 'Failed to load work order'))
      .finally(() => setLoading(false))
  }

  if (!workOrderNumber) return null

  const wo   = data?.wo || {}
  const items = [...(data?.items || [])]
    .filter(item => item.ItemNum || item.ItemDiscrep || item.ItemCorrAction)
    .sort((a, b) => (a.ItemNum ?? 0) - (b.ItemNum ?? 0))
  const parts = [...(data?.parts || [])].sort((a, b) =>
    (Number(a.ItemNumDisplay) || 0) - (Number(b.ItemNumDisplay) || 0)
  )
  const sortedLogs = [...(data?.serviceLogs || [])].sort((a, b) =>
    (a.LogoutDateTime || a.LoginDateTime || '').localeCompare(b.LogoutDateTime || b.LoginDateTime || '')
  )

  // woParts is undefined on old cache entries (no IncludeParts data), [] or [...] on new ones
  const woParts = data?.woParts
  const hasWoParts = Array.isArray(woParts)

  // Build lookup maps for woParts data
  const woPartsMap = {}      // ItemNum → woParts[]
  if (hasWoParts) {
    for (const p of woParts) {
      const key = String(p.ItemNum ?? '')
      if (!key) continue
      if (!woPartsMap[key]) woPartsMap[key] = []
      woPartsMap[key].push(p)
    }
  }

  const customer  = wo.CustomerName || wo.WorkOrderCustomerName || wo.Customer || ''
  const aircraft  = wo.AssetNickname || wo.RegNum || ''
  const woStatus  = wo.WoStatus || ''
  const woType    = wo.WoType || ''
  const created   = wo.WoCreatedDate || wo.DateCreated || ''
  const completed = wo.WoCompletedDate || wo.DateCompleted || ''
  const display   = wo.WorkOrderDisplay || workOrderNumber

  const totalEstHrs    = wo.TotalEstimatedHours ?? null
  const totalLaborHrs  = items.reduce((sum, item) => sum + (Number(item.LaborHrs) || 0), 0)
  const totalLaborBilled  = wo.TotalLaborBilled  ?? null
  const allRejectedItemNums = new Set([
    ...rejectedItemNums,
    ...items.filter(i => REJECTED_ITEM_STATUSES.has(i.ItemStatus)).map(i => String(i.ItemNum ?? '')),
  ])
  const activeItems = items.filter(i => !allRejectedItemNums.has(String(i.ItemNum ?? '')))
  const hasInvSummary = items.some(i => i.invoiceSummary != null)
  const totalPartsBilled = hasInvSummary
    ? activeItems.reduce((s, i) => s + (Number(i.invoiceSummary?.Parts) || 0), 0) || null
    : hasWoParts
      ? woParts.filter(p => !allRejectedItemNums.has(String(p.ItemNum ?? ''))).reduce((sum, p) => sum + (Number(p.QtyNeed) || 0) * (Number(p.FinalUnitPrice) || 0), 0) || null
      : null
  const totalOsrParts = hasInvSummary
    ? activeItems.reduce((s, i) => s + (Number(i.invoiceSummary?.PartsOsrOnly) || 0), 0) || null
    : null
  const totalShipping = hasInvSummary
    ? items.reduce((s, i) => s + (Number(i.invoiceSummary?.ShippingIn) || 0) + (Number(i.invoiceSummary?.ShippingOut) || 0), 0) || null
    : null
  const totalShopSupplies = wo.TotalShopSupplies ?? null
  const totalMiscCharges  = wo.TotalMiscCharges  ?? null
  const totalAmountBilled = wo.TotalAmountBilled ?? null
  const totalAmtDue       = wo.TotalAmtDue       ?? null

  const hasCosts = totalLaborBilled != null || totalPartsBilled != null

  const partsTabCount = hasWoParts ? woParts.length : parts.length

  // For Items tab: per-item parts status summary
  const getItemPartsSummary = (itemNum) => {
    const ps = woPartsMap[String(itemNum)] ?? []
    if (ps.length === 0) return null
    const pending = ps.filter(p => p.PartStatus !== 'Fulfilled')
    return { total: ps.length, pending: pending.length }
  }

  const woPartsItemTotal = (itemNumStr) => {
    if (allRejectedItemNums.has(itemNumStr)) return 0
    const item = items.find(i => String(i.ItemNum ?? '') === itemNumStr)
    if (item?.invoiceSummary?.Parts != null) return Number(item.invoiceSummary.Parts) || 0
    if (!hasWoParts) return null
    return (woPartsMap[itemNumStr] ?? []).reduce((sum, p) => sum + (Number(p.QtyNeed) || 0) * (Number(p.FinalUnitPrice) || 0), 0)
  }

  const itemColCount = hasWoParts ? 10 : 9

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-[78rem] max-w-full bg-white shadow-2xl z-50 flex flex-col">

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0 space-y-3">
          {/* Title row */}
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1.5 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-base font-bold text-gray-900">{display}</span>
                {woStatus && <StatusBadge status={woStatus} />}
                {woType   && <span className="text-xs text-gray-400">{woType}</span>}
              </div>
              <div className="flex items-center gap-x-4 gap-y-1 text-xs text-gray-500 flex-wrap">
                {customer  && <span>{customer}</span>}
                {aircraft  && <span>Aircraft: <span className="font-medium text-gray-700">{aircraft}</span></span>}
                {created   && <span>Created: {created}</span>}
                {completed && completed !== '' && <span>Completed: {completed}</span>}

                {totalEstHrs != null && <span>Est. Hrs: <span className="font-medium text-gray-700">{fmtHrs(totalEstHrs)}</span></span>}
                {items.length > 0 && <span>Actual Hrs: <span className="font-medium text-gray-700">{fmtHrs(totalLaborHrs)}</span></span>}
              </div>
            </div>
            <div className="flex-shrink-0 flex items-center gap-2 ml-4">
              {cachedAt && (
                <span className="text-xs text-gray-400 whitespace-nowrap">
                  Cached · {new Date(cachedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </span>
              )}
              <button
                onClick={handleRefresh}
                disabled={loading}
                title="Fetch live from EBIS"
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-40"
              >
                <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Cost summary strip */}
          {hasCosts && (
            <div className="flex items-center gap-6 pt-1 border-t border-gray-200">
              {totalLaborBilled  != null && <CostStat label="Labor"         value={fmtMoney(totalLaborBilled)} />}
              {totalPartsBilled  != null && <CostStat label="Parts"         value={fmtMoney(totalPartsBilled)} />}
              {totalShipping     != null && totalShipping > 0 && <CostStat label="Shipping"      value={fmtMoney(totalShipping)} />}
              {totalShopSupplies != null && Number(totalShopSupplies) > 0 && <CostStat label="Shop Supplies" value={fmtMoney(totalShopSupplies)} />}
              {totalMiscCharges  != null && Number(totalMiscCharges)  > 0 && <CostStat label="Misc Charges"  value={fmtMoney(totalMiscCharges)} />}
              {totalAmountBilled != null && (
                <div className="flex items-center gap-6 border-l border-gray-300 pl-6 ml-2">
                  <CostStat label="Total Billed" value={fmtMoney(totalAmountBilled)} highlight />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6 flex-shrink-0">
          {[
            { key: 'items',    label: `Work Order Items${items.length ? ` (${items.length})` : ''}` },
            { key: 'parts',    label: `Parts${partsTabCount ? ` (${partsTabCount})` : ''}` },
            { key: 'activity', label: `Tech Activity${sortedLogs.length ? ` (${sortedLogs.length})` : ''}` },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === t.key
                  ? 'border-brand-red text-brand-red'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto min-h-0" ref={bodyScrollRef}>

          {loading && !data && (
            <div className="flex items-center justify-center py-24">
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Loading work order…
              </div>
            </div>
          )}

          {error && (
            <div className="m-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Items tab */}
          {data && !error && tab === 'items' && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 sticky top-0">
                    {['Item', 'Category', 'Discrepancy', 'Corrective Action', 'Status', 'Hrs', 'Labor $', 'Parts $',
                      ...(hasWoParts ? ['Parts'] : []),
                      '',
                    ].map((h, hi) => (
                      <th key={hi} className="px-4 py-2.5 text-left font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={itemColCount} className="px-4 py-10 text-center text-gray-400">No items found</td>
                    </tr>
                  ) : items.map((item, i) => {
                    const itemNumStr   = item.ItemNum != null ? String(item.ItemNum) : String(i + 1)
                    const partsSummary = hasWoParts ? getItemPartsSummary(item.ItemNum) : null
                    const itemHasParts = hasWoParts && (woPartsMap[itemNumStr] ?? []).length > 0
                    const partsOpen    = expandedPartsRows.has(itemNumStr)
                    const attachOpen   = expandedAttachRows.has(itemNumStr)
                    const itemAttachs  = attachments.filter(a => a.entity_type === 'project_item' && a.project_id === resolvedProjectId && a.item_num === itemNumStr)
                    const attachCount  = itemAttachs.length
                    return (
                      <Fragment key={i}>
                        <tr
                          data-itemnum={item.ItemNum}
                          onClick={() => itemHasParts && setExpandedPartsRows(prev => {
                            const next = new Set(prev)
                            next.has(itemNumStr) ? next.delete(itemNumStr) : next.add(itemNumStr)
                            return next
                          })}
                          className={`hover:bg-gray-50 transition-colors ${itemHasParts ? 'cursor-pointer' : ''} ${partsOpen ? 'bg-gray-50/60' : ''} ${flashedItem != null && Number(item.ItemNum) === flashedItem ? 'bg-yellow-100' : ''}`}
                        >
                          <td className="px-4 py-2.5 font-semibold text-gray-700 whitespace-nowrap">
                            {item.ItemNum ?? '—'}
                          </td>
                          <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
                            {item.ItemCat1 || '—'}
                          </td>
                          <td className="px-4 py-2.5 text-gray-700 max-w-[20rem]">
                            <div className="flex items-start gap-1.5">
                              {itemHasParts && (
                                <svg className={`w-3 h-3 text-gray-400 shrink-0 mt-0.5 transition-transform ${partsOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              )}
                              <div className="line-clamp-3" title={item.ItemDiscrep}>
                                {item.ItemDiscrep || '—'}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-gray-600 max-w-[20rem]">
                            <div className="line-clamp-3" title={item.ItemCorrAction}>
                              {item.ItemCorrAction || '—'}
                            </div>
                          </td>
                          <td className="px-4 py-2.5 whitespace-nowrap">
                            <StatusBadge status={item.ItemStatus} />
                          </td>
                          <td className="px-4 py-2.5 text-gray-700 whitespace-nowrap text-right tabular-nums">
                            {fmtHrs(item.LaborHrs ?? item.TotalHoursWorked ?? null)}
                          </td>
                          <td className="px-4 py-2.5 whitespace-nowrap text-right tabular-nums">
                            {item.invoiceSummary?.LaborTotal > 0
                              ? <span className="text-green-700 font-medium">{fmtMoney(item.invoiceSummary.LaborTotal)}</span>
                              : '—'}
                          </td>
                          <td className="px-4 py-2.5 whitespace-nowrap text-right tabular-nums">
                            {(() => {
                              const t = woPartsItemTotal(itemNumStr)
                              return t > 0 ? <span className="text-blue-700 font-medium">{fmtMoney(t)}</span> : '—'
                            })()}
                          </td>
                          {hasWoParts && (
                            <td className="px-4 py-2.5 whitespace-nowrap">
                              {partsSummary == null ? (
                                <span className="text-gray-300">—</span>
                              ) : REJECTED_ITEM_STATUSES.has(item.ItemStatus) ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-400">
                                  {partsSummary.total}
                                </span>
                              ) : partsSummary.pending === 0 ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                                  ✓ {partsSummary.total}
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                                  {partsSummary.pending} pending
                                </span>
                              )}
                            </td>
                          )}
                          <td className="px-2 py-2.5" onClick={e => e.stopPropagation()}>
                            {resolvedProjectId && (
                              <button
                                onClick={() => setExpandedAttachRows(prev => {
                                  const next = new Set(prev)
                                  next.has(itemNumStr) ? next.delete(itemNumStr) : next.add(itemNumStr)
                                  return next
                                })}
                                className={`flex items-center gap-0.5 text-[10px] rounded px-1 py-0.5 transition-colors ${attachOpen ? 'text-teal-600 bg-teal-50' : attachCount > 0 ? 'text-teal-500' : 'text-gray-400 hover:text-gray-600'}`}
                                title="Attachments"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                </svg>
                                {attachCount > 0 && <span>{attachCount}</span>}
                              </button>
                            )}
                          </td>
                        </tr>
                        {partsOpen && itemHasParts && (
                          <tr className="border-b border-gray-100 bg-violet-50/30">
                            <td colSpan={itemColCount} className="px-6 py-3">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="border-b border-gray-200/60">
                                    {['Part Number', 'Description', 'Qty', 'Status', 'Price'].map(h => (
                                      <th key={h} className="pb-1.5 text-center font-semibold text-gray-400 uppercase tracking-wider px-2 first:text-left">{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {(woPartsMap[itemNumStr] ?? []).map((p, pi) => {
                                    const extPrice = p.FinalUnitPrice != null && p.QtyNeed != null
                                      ? Number(p.QtyNeed) * Number(p.FinalUnitPrice)
                                      : null
                                    return (
                                      <tr key={pi} className="border-b border-gray-100/60 last:border-0">
                                        <td className="py-1.5 px-2 pl-0 font-mono text-gray-800 whitespace-nowrap">{p.PartNumber || '—'}</td>
                                        <td className="py-1.5 px-2 text-center text-gray-700 max-w-[14rem]">
                                          <div className="line-clamp-2" title={p.Description}>{p.Description || '—'}</div>
                                        </td>
                                        <td className="py-1.5 px-2 text-center tabular-nums whitespace-nowrap">{p.QtyNeed != null ? Number(p.QtyNeed).toLocaleString() : '—'}</td>
                                        <td className="py-1.5 px-2 text-center whitespace-nowrap"><PartStatusBadge status={p.PartStatus} /></td>
                                        <td className="py-1.5 px-2 text-center tabular-nums whitespace-nowrap">
                                          {extPrice != null ? <span className="font-medium text-gray-900">{fmtMoney(extPrice)}</span> : <span className="text-gray-300">—</span>}
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                        {attachOpen && resolvedProjectId && (
                          <tr className="border-b border-gray-100 bg-teal-50/20">
                            <td colSpan={itemColCount} className="p-0">
                              <ItemAttachments
                                entityType="project_item"
                                projectId={resolvedProjectId}
                                itemNum={itemNumStr}
                                allAttachments={attachments}
                                onAttachmentAdded={a => setAttachments(prev => [...prev, a])}
                                onAttachmentDeleted={id => setAttachments(prev => prev.filter(a => a.id !== id))}
                                canUpload={!!profileId}
                                profileId={profileId}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
                {items.length > 1 && (() => {
                  const totalItemsLabor = items.reduce((s, it) => s + (Number(it.invoiceSummary?.LaborTotal) || 0), 0)
                  return (
                    <tfoot>
                      <tr className="bg-gray-50 border-t-2 border-gray-200 font-semibold text-gray-700">
                        <td colSpan={5} className="px-4 py-2.5 text-xs uppercase tracking-wide text-gray-400">Totals</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-xs">
                          {fmtHrs(totalLaborHrs)}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-xs">
                          {totalItemsLabor > 0 ? fmtMoney(totalItemsLabor) : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-xs">
                          {totalPartsBilled != null ? fmtMoney(totalPartsBilled) : '—'}
                        </td>
                        {hasWoParts && <td />}
                        <td />
                      </tr>
                    </tfoot>
                  )
                })()}
              </table>
            </div>
          )}

          {/* Parts tab */}
          {data && !error && tab === 'parts' && (
            !hasWoParts ? (
              /* Old cache entry — no woParts data, prompt refresh */
              <div className="m-6 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 flex items-start gap-3">
                <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-amber-800">Parts status unavailable</p>
                  <p className="text-xs text-amber-700 mt-1">
                    This work order was cached before parts status tracking was added.
                    Refresh to load operational parts data.
                  </p>
                  <button
                    onClick={handleRefresh}
                    disabled={loading}
                    className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-700 text-white text-xs font-medium hover:bg-amber-800 transition-colors disabled:opacity-50"
                  >
                    <svg className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh now
                  </button>
                </div>
              </div>
            ) : (
              /* New cache entry — show operational woParts data */
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 sticky top-0">
                      {['Item', 'Part Number', 'Description', 'Qty', 'Status', 'PO #', 'Ext. Price'].map(h => (
                        <th key={h} className="px-4 py-2.5 text-left font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {woParts.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-10 text-center text-gray-400">No parts recorded for this work order</td>
                      </tr>
                    ) : (() => {
                      const sortedWoParts = [...woParts].sort((a, b) =>
                        (Number(a.ItemNum) || 0) - (Number(b.ItemNum) || 0)
                      )
                      let lastItem = null
                      return sortedWoParts.map((p, i) => {
                        const itemNum = p.ItemNum ?? '—'
                        const showItemHeader = String(itemNum) !== String(lastItem)
                        if (showItemHeader) lastItem = itemNum

                        // Find discrepancy from the items array
                        const itemObj = items.find(it => String(it.ItemNum) === String(itemNum))
                        const discrep = itemObj?.ItemDiscrep || ''

                        const extPrice = p.FinalUnitPrice != null && p.QtyNeed != null
                          ? Number(p.QtyNeed) * Number(p.FinalUnitPrice)
                          : null

                        return (
                          <Fragment key={i}>
                            {showItemHeader && (
                              <tr className="bg-gray-50 border-y border-gray-200">
                                <td colSpan={7} className="px-4 py-1.5 text-xs font-semibold text-gray-600">
                                  Item {itemNum}{discrep ? ` — ${discrep}` : ''}
                                </td>
                              </tr>
                            )}
                            <tr className="hover:bg-gray-50">
                              <td className="px-4 py-2.5 font-semibold text-gray-600 whitespace-nowrap">{itemNum}</td>
                              <td className="px-4 py-2.5 font-mono text-gray-800 whitespace-nowrap">{p.PartNumber || '—'}</td>
                              <td className="px-4 py-2.5 text-gray-700 max-w-[14rem]">
                                <div className="line-clamp-2" title={p.Description}>{p.Description || '—'}</div>
                              </td>
                              <td className="px-4 py-2.5 text-gray-700 whitespace-nowrap tabular-nums text-right">
                                {p.QtyNeed != null ? Number(p.QtyNeed).toLocaleString() : '—'}
                              </td>
                              <td className="px-4 py-2.5 whitespace-nowrap">
                                <PartStatusBadge status={p.PartStatus} />
                              </td>
                              <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap font-mono">
                                {p.Po || '—'}
                              </td>
                              <td className="px-4 py-2.5 whitespace-nowrap tabular-nums text-right">
                                {extPrice != null
                                  ? <span className="font-medium text-gray-900">{fmtMoney(extPrice)}</span>
                                  : <span className="text-gray-300">—</span>}
                              </td>
                            </tr>
                          </Fragment>
                        )
                      })
                    })()}
                  </tbody>
                  {woParts.length > 0 && (() => {
                    const billedTotal = woParts.reduce((s, p) =>
                      s + (Number(p.QtyNeed) || 0) * (Number(p.FinalUnitPrice) || 0), 0)
                    return billedTotal > 0 ? (
                      <tfoot>
                        <tr className="bg-gray-50 border-t-2 border-gray-200 font-semibold text-gray-700">
                          <td colSpan={8} className="px-4 py-2.5 text-xs uppercase tracking-wide text-gray-400">Total Parts Billed</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-xs">
                            {fmtMoney(billedTotal)}
                          </td>
                        </tr>
                      </tfoot>
                    ) : null
                  })()}
                </table>
              </div>
            )
          )}

          {/* Tech Activity tab */}
          {data && !error && tab === 'activity' && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 sticky top-0">
                    {['Technician', 'Total Hrs', 'Logged In', 'Logged Out', 'Item', 'Discrepancy'].map(h => (
                      <th key={h} className="px-4 py-2.5 text-left font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sortedLogs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-gray-400">No tech activity recorded</td>
                    </tr>
                  ) : sortedLogs.map((log, i) => {
                    const logDate = (log.LogoutDate || log.LogoutDateTime || '').slice(0, 10)
                    const prevDate = i > 0 ? (sortedLogs[i - 1].LogoutDate || sortedLogs[i - 1].LogoutDateTime || '').slice(0, 10) : null
                    const showDateHeader = logDate && logDate !== prevDate
                    return (
                      <Fragment key={i}>
                        {showDateHeader && (
                          <tr className="bg-gray-50 border-y border-gray-200">
                            <td colSpan={6} className="px-4 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              {fmtDateHeading(logDate)}
                            </td>
                          </tr>
                        )}
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-2.5 font-medium text-gray-700 whitespace-nowrap">
                            {log.Technician || '—'}
                          </td>
                          <td className="px-4 py-2.5 text-gray-700 whitespace-nowrap text-right tabular-nums">
                            {fmtHrs(log.TotalHours)}
                          </td>
                          <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">
                            {fmtDateTime(log.LoginDateTime)}
                          </td>
                          <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">
                            {fmtDateTime(log.LogoutDateTime)}
                          </td>
                          <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
                            {log.ItemNumber ?? log.ItemNum ?? '—'}
                          </td>
                          <td className="px-4 py-2.5 text-gray-600 max-w-[14rem]">
                            <div className="line-clamp-2" title={log.Discrep}>
                              {log.Discrep || '—'}
                            </div>
                          </td>
                        </tr>
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

        </div>
      </div>
    </>
  )
}
